// src/context/CartContext.js
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

const CartContext = createContext(null);

const resolveDealId = (productOrId) => {
  if (!productOrId) return null;
  if (typeof productOrId === "string") return productOrId;

  // si product.dealId est un objet
  if (productOrId.dealId && typeof productOrId.dealId === "object") {
    return productOrId.dealId._id || productOrId.dealId.id || null;
  }

  // si product.dealId est une string
  if (productOrId.dealId && typeof productOrId.dealId === "string") {
    return productOrId.dealId;
  }

  // fallback
  return productOrId._id || productOrId.id || productOrId.slug || null;
};

const normalizeCartItem = (product, quantity = 1, idx = 0) => {
  const dealId = resolveDealId(product);
  const qty = Math.max(1, Math.floor(Number(quantity) || 1));
  const price = Number(product?.price ?? product?.discountedPrice ?? 0) || 0;

  return {
    _localKey: product?._localKey || `${dealId || "x"}-${idx}`,
    dealId, // ✅ stable pour Checkout
    quantity: qty,
    price,
    title: product?.title || (product?.dealId && product.dealId.title) || "Deal",
    image:
      product?.image ||
      (Array.isArray(product?.images) && product.images[0]) ||
      (product?.dealId && product.dealId.images && product.dealId.images[0]) ||
      "",
    _id: product?._id || dealId, // compat
  };
};

export const CartProvider = ({ children }) => {
  const mountedRef = useRef(true);
  const [cartItems, setCartItems] = useState([]);

  // Load cart from localStorage once
  useEffect(() => {
    mountedRef.current = true;
    try {
      const savedCart = localStorage.getItem("cart");
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        if (Array.isArray(parsed)) {
          const normalized = parsed
            .map((it, idx) => normalizeCartItem(it, it?.quantity ?? 1, idx))
            .filter((it) => it.dealId);
          setCartItems(normalized);
        }
      }
    } catch (e) {
      // ignore
    }
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Persist cart to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem("cart", JSON.stringify(cartItems));
    } catch (e) {
      // ignore
    }
  }, [cartItems]);

  const addToCart = (product, quantity = 1) => {
    const dealId = resolveDealId(product);
    if (!dealId) return;

    const qty = Math.max(1, Math.floor(Number(quantity) || 1));

    setCartItems((prev) => {
      const existing = prev.find((item) => String(item.dealId) === String(dealId));
      if (existing) {
        return prev.map((item) =>
          String(item.dealId) === String(dealId)
            ? { ...item, quantity: (Number(item.quantity) || 0) + qty }
            : item
        );
      }
      const newItem = normalizeCartItem(product, qty, prev.length);
      return [...prev, newItem];
    });
  };

  const removeFromCart = (productOrId) => {
    const dealId = resolveDealId(productOrId);
    if (!dealId) return;
    setCartItems((prev) => prev.filter((item) => String(item.dealId) !== String(dealId)));
  };

  const updateQuantity = (productOrId, quantity) => {
    const dealId = resolveDealId(productOrId);
    if (!dealId) return;

    const qty = Math.floor(Number(quantity) || 0);
    if (qty <= 0) {
      setCartItems((prev) => prev.filter((item) => String(item.dealId) !== String(dealId)));
      return;
    }

    setCartItems((prev) =>
      prev.map((item) =>
        String(item.dealId) === String(dealId) ? { ...item, quantity: qty } : item
      )
    );
  };

  const clearCart = () => setCartItems([]);

  const cartCount = useMemo(
    () => cartItems.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0),
    [cartItems]
  );

  const cartTotal = useMemo(
    () =>
      cartItems.reduce((acc, item) => {
        const price = Number(item.price ?? 0);
        const qty = Number(item.quantity) || 0;
        return acc + price * qty;
      }, 0),
    [cartItems]
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartCount,
        cartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart doit être utilisé à l'intérieur de CartProvider");
  return ctx;
};
